import { useState } from 'react';
import { Calendar, CheckCircle2, Clock, Mail, MapPin, Package, Users } from 'lucide-react';
import { getRoleBadgeClasses, getRoleLabel } from '@/utils/role';
import { DonutLegendChart } from '@/components/ui';
import { formatDate, formatDateTime } from '@/utils/format';
import type { WorkOrderDetailViewModel } from '../types/workOrderDetail.types';
import type { ElementType, ReactNode } from 'react';

interface DetailOverviewTabProps {
  data: WorkOrderDetailViewModel;
}

export function DetailOverviewTab({ data }: DetailOverviewTabProps) {
  const [isDescExpanded, setIsDescExpanded] = useState(false);
  const totalDays = getTimelineDays(data.plannedStart, data.plannedEnd);
  const completedPercent = data.status === 'completed' || data.status === 'closed' ? 100 : data.progress;
  const inProgressPercent = data.status === 'completed' || data.status === 'closed' ? 0 : Math.max(0, 100 - completedPercent);
  const pendingPercent = data.progress === 0 ? 100 : 0;
  const overduePercent = data.status === 'overdue' ? Math.max(0, 100 - data.progress) : 0;

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
      <div className="space-y-4">
        <Block title="Mô tả công việc">
          <div className="space-y-2">
            <p className={`text-sm font-normal leading-relaxed text-slate-600 whitespace-pre-wrap ${!isDescExpanded ? 'line-clamp-3' : ''}`}>
              {data.description || 'Chưa có mô tả công việc.'}
            </p>
            {data.description && data.description.length > 150 && (
              <button 
                type="button" 
                onClick={() => setIsDescExpanded(!isDescExpanded)}
                className="inline-flex items-center gap-1 sm:text-sm text-xs font-medium text-emerald-600 hover:text-emerald-800 transition-colors"
              >
                {isDescExpanded ? 'Thu gọn' : 'Xem thêm'}
                <i className={isDescExpanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
              </button>
            )}
          </div>
        </Block>

        <Block title="Thông tin thi công">
          <div className="space-y-2">
            <InfoRow icon={Package} label="Hạng mục" value={data.boqName} />
            <InfoRow icon={Users} label="Đội phụ trách" value={data.teamName} />
            <InfoRow icon={MapPin} label="Vị trí" value={`${data.projectName} - ${data.zoneName}`} />
            <InfoRow icon={Calendar} label="Kế hoạch" value={`${formatDate(data.plannedStart)} - ${formatDate(data.plannedEnd)}`} />
            <InfoRow icon={Clock} label="Thực tế" value={`${formatDate(data.actualStart)} - ${formatDate(data.actualEnd)}`} />
          </div>
        </Block>

        {data.workTypeLabel === 'Bảo dưỡng' && (
          <Block title="Thông số bảo dưỡng">
            <div className="grid grid-cols-2 gap-4">
              <InfoRow icon={Package} label="Tổng diện tích" value={`${data.area_m2 || 0} m²`} />
              <InfoRow icon={Package} label="Số lượng cây" value={`${data.tree_count || 0} gốc`} />
              <InfoRow icon={Package} label="Diện tích cỏ" value={`${data.lawn_area_m2 || 0} m²`} />
              <InfoRow icon={Package} label="Diện tích bụi" value={`${data.shrub_area_m2 || 0} m²`} />
              <InfoRow icon={Package} label="Dài hàng rào" value={`${data.hedge_length_m || 0} m`} />
              <InfoRow icon={Package} label="Lượng vật tư" value={data.material_quantity || '---'} />
            </div>
          </Block>
        )}

        <Block title="Người phụ trách">
          <div className="flex items-center gap-3">
            <Avatar name={data.leadName} src={data.leadAvatar} />
            <div className="min-w-0 space-y-2">
              <div className="flex items-center gap-2">
                <p className="truncate text-sm font-normal text-slate-900">{data.leadName}</p>
                <span className={getRoleBadgeClasses(data.leadRoleCode)}>
                  {data.leadRoleName}
                </span>
              </div>
              <p className="flex items-center gap-2 truncate text-sm font-normal text-slate-500">
                <Mail className="h-4 w-4 shrink-0" />
                {data.leadEmail || 'Chưa có email'}
              </p>
            </div>
          </div>
        </Block>

        {data.members && data.members.length > 0 && (
          <Block title="Người phối hợp">
            <div className="space-y-4">
              {data.members.map((member, idx) => (
                <div key={member.id || idx} className="flex items-center gap-3">
                  <Avatar 
                    name={member.user?.full_name || 'Thành viên'} 
                    src={member.user?.avatar_path} 
                  />
                  <div className="min-w-0 space-y-2">
                    <div className="flex items-center gap-2">
                      <p className="truncate text-sm font-normal text-slate-900">
                        {member.user?.full_name || 'N/A'}
                      </p>
                      <span className={getRoleBadgeClasses(member.user?.role_code || '')}>
                        {getRoleLabel(member.user?.role_code || '')}
                      </span>
                    </div>
                    <p className="flex items-center gap-2 truncate text-sm font-normal text-slate-500">
                      <Mail className="h-4 w-4 shrink-0" />
                      {member.user?.email || 'Chưa có email'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </Block>
        )}
      </div>

      <div className="space-y-4">
        <DonutLegendChart
          title="Tiến độ công việc"
          centerValue={`${data.progress}%`}
          centerLabel={data.statusLabel}
          total={100}
          data={[
            { name: 'Hoàn thành', value: completedPercent, color: '#047857' },
            { name: 'Đang thực hiện', value: inProgressPercent, color: '#2563eb' },
            { name: 'Chưa bắt đầu', value: pendingPercent, color: '#f59e0b' },
            { name: 'Quá hạn', value: overduePercent, color: '#e11d48' },
          ]}
          showLegendPercent={false}
          valueFormatter={(item) => {
            const days = Math.round((item.value / 100) * totalDays);
            return `${item.value}% (${days}/${totalDays} ngày)`;
          }}
        />

        <Block title="Mốc thời gian">
          <div className="relative space-y-2 py-2 pl-8 before:absolute before:bottom-6 before:left-5 before:top-6 before:w-px before:bg-slate-200">
            <TimelineItem 
              title="Ngày bắt đầu" 
              date={formatDateTime(data.plannedStart)} 
              status={['draft', 'pending', 'not_started'].includes(data.status) ? 'empty' : 'done'} 
            />
            <TimelineItem 
              title="Ngày kết thúc dự kiến" 
              date={formatDateTime(data.plannedEnd)} 
              status={['completed', 'closed'].includes(data.status) ? 'done' : 'next'} 
            />
            <TimelineItem 
              title="Mốc: Nghiệm thu công việc" 
              date={formatDateTime(data.dueDate)} 
              status={data.status === 'completed' || data.status === 'closed' ? 'done' : 'next'} 
              badge={data.dueDate && new Date(data.dueDate) > new Date() && !['completed', 'closed'].includes(data.status) ? 'Sắp tới' : undefined} 
            />
            <TimelineItem 
              title="Ngày kết thúc thực tế" 
              date={data.actualEnd ? formatDateTime(data.actualEnd) : '---'} 
              status={data.actualEnd ? 'done' : 'empty'} 
            />
            <TimelineItem 
              title="Cập nhật lần cuối" 
              date={data.updatedAt ? formatDateTime(data.updatedAt) : '---'} 
              status="empty" 
            />
          </div>
        </Block>

        <Block title="Nhật ký gần đây">
          <div className="space-y-2">
            {data.logs.slice(0, 4).map((log, index) => (
              <div key={log.id || index} className="rounded-md bg-slate-50 p-3">
                <div className="flex justify-between items-start gap-2">
                  <p className="text-sm font-normal text-slate-800">
                    {log.summary || log.title || log.note || log.description || 'Cập nhật tiến độ'}
                  </p>
                  <span className="shrink-0 text-[10px] font-normal text-slate-400">
                    {log.user?.full_name || 'Hệ thống'}
                  </span>
                </div>
                <p className="mt-1 sm:text-sm text-xs text-slate-500">{formatDateTime(log.created_at)}</p>
              </div>
            ))}
            {data.logs.length === 0 ? <EmptyLine text="Chưa có nhật ký tiến độ." /> : null}
          </div>
        </Block>
      </div>
    </div>
  );
}

function Block({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-2 rounded-md border border-slate-100 bg-white p-3">
      <h2 className="text-sm font-normal text-slate-950">{title}</h2>
      {children}
    </section>
  );
}

function InfoRow({ icon: Icon, label, value }: { icon: ElementType; label: string; value: string }) {
  return (
    <div className="grid grid-cols-[24px_120px_1fr] items-start gap-2 rounded-md bg-slate-50 p-3">
      <Icon className="h-4 w-4 text-slate-400" />
      <span className="text-sm font-normal text-slate-500">{label}</span>
      <span className="min-w-0 break-words text-sm font-normal text-slate-800">{value || '---'}</span>
    </div>
  );
}

function Avatar({ name, src }: { name: string; src?: string | null }) {
  return (
    <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md bg-slate-100 text-sm font-normal text-slate-600">
      {src ? <img src={src} alt={name} className="h-full w-full object-cover" /> : name.slice(0, 1)}
    </div>
  );
}

function TimelineItem({
  title,
  date,
  status,
  badge,
}: {
  title: string;
  date: string;
  status: 'done' | 'next' | 'empty';
  badge?: string;
}) {
  const isDone = status === 'done';
  const isNext = status === 'next';

  return (
    <div className="relative grid grid-cols-[1fr_auto] items-center gap-3 rounded-md p-3">
      <div
        className={`absolute -left-8 top-1/2 z-10 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full ring-4 ${
          isDone
            ? 'bg-emerald-100 text-emerald-600 ring-emerald-50'
            : isNext
              ? 'bg-slate-200 text-slate-500 ring-slate-50'
              : 'bg-slate-100 text-slate-400 ring-white'
        }`}
      >
        {isDone ? <CheckCircle2 className="h-4 w-4" /> : <span className="h-1.5 w-1.5 rounded-full bg-current" />}
      </div>

      <div className="flex min-w-0 items-center gap-2">
        <span className="truncate text-sm font-normal text-slate-700">{title}</span>
        {badge ? <span className="shrink-0 whitespace-nowrap rounded-md bg-blue-50 px-2 py-1 sm:text-sm text-xs text-blue-600">{badge}</span> : null}
      </div>
      <span className="whitespace-nowrap text-sm font-normal text-slate-500">{date}</span>
    </div>
  );
}

function EmptyLine({ text }: { text: string }) {
  return <p className="rounded-md bg-slate-50 p-3 text-sm font-normal text-slate-500">{text}</p>;
}

function getTimelineDays(start?: string | null, end?: string | null) {
  if (!start || !end) return 10;
  const startDate = new Date(start);
  const endDate = new Date(end);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return 10;
  const diff = endDate.getTime() - startDate.getTime();
  return Math.max(1, Math.ceil(diff / 86_400_000));
}
